import { useCallback, useRef } from "react";

/*
 * Re-sign a page's image URLs, at most now and then.
 *
 * WHY THIS IS NOT JUST `invalidateQueries`. Screenshot URLs are signed in
 * batches: every link in one response is minted within the same second, so when
 * one expires they all have, and refetching the list re-signs the lot. That is
 * the recovery ProxyImage's `onExpired` is for.
 *
 * The trap is that the refetch CHANGES EVERY URL. ProxyImage guards itself with
 * "one retry per URL", which sounds sufficient and is not:
 *
 *     image 404s → onExpired → refetch → every src is re-signed
 *               → the broken one now has a new src
 *               → its per-URL guard is clear again → onExpired → refetch → …
 *
 * One permanently missing object — a capture deleted by retention, a failed
 * upload — is enough to spin that forever. Measured on the Screenshots page:
 * 337 requests in six idle seconds, 117 kB each, from a single dead thumbnail.
 * Re-signing cannot fix a missing object, so the loop had no exit.
 *
 * So the refresh is BOUNDED, in two ways that a genuine expiry survives and a
 * loop does not:
 *
 *   interval  a real batch expiry needs ONE refetch; the URLs last fifteen
 *             minutes, so refusing more than one a minute costs nothing.
 *   count     if freshly signed URLs keep failing, signing is not the problem
 *             and asking again cannot help. Give up and let the fallback show.
 *
 * `key` resets both when the page moves to another day or user, because that is
 * a different set of URLs and deserves its own attempts.
 */
export function useUrlRefresh(refresh, key, { minIntervalMs = 60_000, maxAttempts = 3 } = {}) {
    const state = useRef({ key: null, at: 0, attempts: 0 });

    return useCallback(() => {
        const now = Date.now();
        const current = state.current;

        if (current.key !== key) {
            current.key = key;
            current.at = 0;
            current.attempts = 0;
        }

        if (current.attempts >= maxAttempts) return;
        if (now - current.at < minIntervalMs) return;

        current.at = now;
        current.attempts += 1;
        refresh();
    }, [refresh, key, minIntervalMs, maxAttempts]);
}
